import { submitDemoRequest } from "../app/api/demoRequestApi";
import {
  TOOLKIT_NAV,
  TOOLKIT_PAGE_SUBTITLE,
  estimateToolkitReadingMinutes,
  getToolkitFeatureCount,
  getToolkitSectionById,
  toolkitFaqItems,
  toolkitSections,
  type ToolkitSection,
} from "../app/landing/toolkitContent";
import { applyToolkitGeoSeo } from "./geoSeo";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function isToolkitPath(): boolean {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  return path === "/toolkit" || path.startsWith("/toolkit/");
}

export function getToolkitSlug(): string {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (path === "/toolkit") return "overview";
  const match = path.match(/^\/toolkit\/([^/]+)$/);
  const slug = match?.[1] ?? "overview";
  return (getToolkitSectionById(slug) || slug === "faq") ? slug : "overview";
}

export function toolkitSectionUrl(slug: string): string {
  return slug === "overview" ? "/toolkit" : `/toolkit/${slug}`;
}

function renderRelatedLinks(relatedIds: string[]): string {
  if (!relatedIds.length) return "";
  const links = relatedIds
    .map((id) => {
      const sec = getToolkitSectionById(id);
      if (!sec) return "";
      return `<a href="${escapeHtml(toolkitSectionUrl(id))}" class="toolkit-related-link" data-toolkit-nav="${escapeHtml(id)}">${escapeHtml(sec.title)}</a>`;
    })
    .filter(Boolean)
    .join("");
  return `<div class="toolkit-related"><span class="toolkit-related-label">Related</span><div class="toolkit-related-links">${links}</div></div>`;
}

function renderCallout(callout: NonNullable<ToolkitSection["callout"]>): string {
  return `<aside class="toolkit-callout toolkit-callout--${escapeHtml(callout.type)}"><p>${escapeHtml(callout.text)}</p></aside>`;
}

function renderScreenshotPlaceholder(): string {
  return `<div class="toolkit-screenshot-placeholder" aria-label="Screenshot placeholder"><span>Screenshot coming soon</span></div>`;
}

function renderDemoCallout(): string {
  return `<aside class="toolkit-demo-callout"><p>See this feature in action.</p><button type="button" class="pricing-tier-cta is-secondary" data-open-demo-request>Book a Demo</button></aside>`;
}

function renderSectionBody(section: ToolkitSection): string {
  const steps = section.howToUse
    .map((step, i) => `<li>${escapeHtml(step)}</li>`)
    .join("");
  const tips = section.proTips.map((tip) => `<li>${escapeHtml(tip)}</li>`).join("");
  const subsections =
    section.subsections
      ?.map(
        (sub) =>
          `<div class="toolkit-subsection"><h3>${escapeHtml(sub.title)}</h3><p>${escapeHtml(sub.body)}</p></div>`,
      )
      .join("") ?? "";
  const shortcuts = section.shortcuts?.length
    ? `<div class="toolkit-shortcuts-wrap"><table class="toolkit-shortcuts-table"><thead><tr><th scope="col">Shortcut</th><th scope="col">Action</th></tr></thead><tbody>${section.shortcuts
        .map(
          (row) =>
            `<tr><td><kbd>${escapeHtml(row.keys)}</kbd></td><td>${escapeHtml(row.action)}</td></tr>`,
        )
        .join("")}</tbody></table></div>`
    : "";

  return `
    <p class="toolkit-section-lead">${escapeHtml(section.description)}</p>
    ${section.callout ? renderCallout(section.callout) : ""}
    <h3 class="toolkit-section-heading">Why It Matters</h3>
    <p>${escapeHtml(section.whyItMatters)}</p>
    <h3 class="toolkit-section-heading">How To Use It</h3>
    <ol class="toolkit-steps">${steps}</ol>
    <h3 class="toolkit-section-heading">Pro Tips</h3>
    <ul class="toolkit-tips">${tips}</ul>
    ${subsections}
    ${shortcuts}
    ${renderScreenshotPlaceholder()}
    ${renderRelatedLinks(section.relatedIds)}
  `;
}

function renderToolkitSection(section: ToolkitSection, featureIndex: number): string {
  const showDemo =
    !section.isOverview &&
    !section.isFaq &&
    featureIndex >= 2 &&
    (featureIndex - 2) % 3 === 0;

  return `<section id="${escapeHtml(section.id)}" class="toolkit-section" data-toolkit-section="${escapeHtml(section.id)}">
    <div class="toolkit-section-header">
      <h2>${escapeHtml(section.title)}</h2>
      <button type="button" class="toolkit-copy-link" data-copy-slug="${escapeHtml(section.id)}" aria-label="Copy link to ${escapeHtml(section.title)}">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        <span class="toolkit-copy-link-label">Copy link</span>
      </button>
    </div>
    ${renderSectionBody(section)}
    ${showDemo ? renderDemoCallout() : ""}
  </section>`;
}

function renderFaqSection(): string {
  const items = toolkitFaqItems
    .map(
      (item) =>
        `<details class="toolkit-faq-item" data-toolkit-search="${escapeHtml(`${item.question} ${item.answer}`)}" id="${escapeHtml(item.id)}"><summary>${escapeHtml(item.question)}</summary><p>${escapeHtml(item.answer)}</p></details>`,
    )
    .join("");

  return `<section id="faq" class="toolkit-section toolkit-section--faq" data-toolkit-section="faq">
    <div class="toolkit-section-header">
      <h2>FAQ</h2>
      <button type="button" class="toolkit-copy-link" data-copy-slug="faq" aria-label="Copy link to FAQ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        <span class="toolkit-copy-link-label">Copy link</span>
      </button>
    </div>
    <p class="toolkit-section-lead">Common questions about building, reviewing, and shipping emails with Email Perfect.</p>
    <div class="toolkit-faq-controls">
      <button type="button" id="toolkit-faq-expand-all">Expand all</button>
      <span aria-hidden="true">·</span>
      <button type="button" id="toolkit-faq-collapse-all">Collapse all</button>
    </div>
    <div class="toolkit-faq-list">${items}</div>
  </section>`;
}

function renderSidebarNav(): string {
  return TOOLKIT_NAV.map(
    (item) =>
      `<a href="${escapeHtml(toolkitSectionUrl(item.id))}" class="toolkit-sidebar-link" data-toolkit-nav="${escapeHtml(item.id)}">${escapeHtml(item.label)}</a>`,
  ).join("");
}

type SearchEntry = { slug: string; label: string; text: string };

function buildSearchIndex(): SearchEntry[] {
  const entries: SearchEntry[] = [];
  for (const section of toolkitSections) {
    entries.push({
      slug: section.id,
      label: section.title,
      text: [
        section.title,
        section.description,
        section.whyItMatters,
        ...section.howToUse,
        ...section.proTips,
        ...(section.subsections?.flatMap((s) => [s.title, s.body]) ?? []),
      ].join(" "),
    });
  }
  for (const faq of toolkitFaqItems) {
    entries.push({ slug: "faq", label: faq.question, text: `${faq.question} ${faq.answer}` });
  }
  return entries;
}

const searchIndex = buildSearchIndex();

function getHeaderOffset(): number {
  const header = document.getElementById("marketing-header");
  const searchWrap = document.getElementById("toolkit-search-wrap");
  const headerH = header?.offsetHeight ?? 80;
  const searchH = searchWrap?.offsetHeight ?? 0;
  return headerH + (window.innerWidth >= 1024 ? searchH + 16 : 0);
}

function scrollToToolkitSlug(slug: string, smooth = true): void {
  const el = document.getElementById(slug);
  if (!el) return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const top = el.getBoundingClientRect().top + window.scrollY - getHeaderOffset() - 12;
  window.scrollTo({ top: Math.max(0, top), behavior: smooth && !reduceMotion ? "smooth" : "auto" });
  setActiveNav(slug);
}

function setActiveNav(slug: string): void {
  document.querySelectorAll<HTMLElement>(".toolkit-sidebar-link").forEach((link) => {
    const isActive = link.dataset.toolkitNav === slug;
    if (link.classList.contains("is-active") === isActive) return;
    link.classList.toggle("is-active", isActive);
  });
}

function navigateToSlug(slug: string, push = true): void {
  const url = toolkitSectionUrl(slug);
  if (push) {
    window.history.pushState({ toolkitSlug: slug }, "", url);
  }
  applyToolkitGeoSeo(slug);
  scrollToToolkitSlug(slug);
  closeMobileNav();
}

function closeMobileNav(): void {
  const sidebar = document.getElementById("toolkit-sidebar");
  const backdrop = document.getElementById("toolkit-sidebar-backdrop");
  const toggle = document.getElementById("toolkit-mobile-nav-toggle");
  sidebar?.classList.remove("is-open");
  backdrop?.setAttribute("hidden", "");
  toggle?.setAttribute("aria-expanded", "false");
}

function openMobileNav(): void {
  const sidebar = document.getElementById("toolkit-sidebar");
  const backdrop = document.getElementById("toolkit-sidebar-backdrop");
  const toggle = document.getElementById("toolkit-mobile-nav-toggle");
  sidebar?.classList.add("is-open");
  backdrop?.removeAttribute("hidden");
  toggle?.setAttribute("aria-expanded", "true");
}

function setupToolkitNavigation(): void {
  document.querySelectorAll<HTMLAnchorElement>("[data-toolkit-nav]").forEach((link) => {
    link.addEventListener("click", (event) => {
      const slug = link.dataset.toolkitNav;
      if (!slug) return;
      event.preventDefault();
      navigateToSlug(slug);
    });
  });

  window.addEventListener("popstate", () => {
    const slug = getToolkitSlug();
    applyToolkitGeoSeo(slug);
    scrollToToolkitSlug(slug, false);
  });
}

function setupToolkitScrollSpy(): void {
  const sections = [...document.querySelectorAll<HTMLElement>("[data-toolkit-section]")];
  if (!sections.length) return;

  let ticking = false;

  const updateActiveSection = (): void => {
    const offset = getHeaderOffset() + 20;
    let activeSlug = sections[0]?.dataset.toolkitSection ?? "overview";

    for (const section of sections) {
      if (section.getBoundingClientRect().top <= offset) {
        activeSlug = section.dataset.toolkitSection ?? activeSlug;
      } else {
        break;
      }
    }

    const current = document.querySelector<HTMLElement>(".toolkit-sidebar-link.is-active")?.dataset
      .toolkitNav;
    if (current !== activeSlug) {
      setActiveNav(activeSlug);
    }
    ticking = false;
  };

  const scheduleUpdate = (): void => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateActiveSection);
  };

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", scheduleUpdate);
  updateActiveSection();
}

function setupToolkitSearch(): void {
  const input = document.getElementById("toolkit-search") as HTMLInputElement | null;
  const results = document.getElementById("toolkit-search-results");
  if (!input || !results) return;

  let debounce: number | undefined;

  const renderResults = (query: string): void => {
    const q = query.trim().toLowerCase();
    if (!q) {
      results.hidden = true;
      results.innerHTML = "";
      document.querySelectorAll(".toolkit-sidebar-link").forEach((link) => {
        (link as HTMLElement).style.display = "";
      });
      return;
    }

    const matches = searchIndex.filter((entry) => entry.text.toLowerCase().includes(q)).slice(0, 8);

    document.querySelectorAll<HTMLElement>(".toolkit-sidebar-link").forEach((link) => {
      const slug = link.dataset.toolkitNav ?? "";
      const match = searchIndex.some(
        (entry) => entry.slug === slug && entry.text.toLowerCase().includes(q),
      );
      link.style.display = match ? "" : "none";
    });

    if (!matches.length) {
      results.hidden = false;
      results.innerHTML = `<p class="toolkit-search-empty">No matches</p>`;
      return;
    }

    results.hidden = false;
    results.innerHTML = matches
      .map(
        (m) =>
          `<button type="button" class="toolkit-search-result" data-search-slug="${escapeHtml(m.slug)}"><span class="toolkit-search-result-label">${escapeHtml(m.label)}</span></button>`,
      )
      .join("");

    results.querySelectorAll<HTMLButtonElement>(".toolkit-search-result").forEach((btn) => {
      btn.addEventListener("click", () => {
        const slug = btn.dataset.searchSlug;
        if (slug) {
          input.value = "";
          renderResults("");
          navigateToSlug(slug);
        }
      });
    });
  };

  input.addEventListener("input", () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => renderResults(input.value), 150);
  });

  input.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const q = input.value.trim().toLowerCase();
    const first = searchIndex.find((entry) => entry.text.toLowerCase().includes(q));
    if (first) {
      input.value = "";
      renderResults("");
      navigateToSlug(first.slug);
    }
  });
}

function setupCopyLinks(): void {
  document.querySelectorAll<HTMLButtonElement>(".toolkit-copy-link").forEach((button) => {
    button.addEventListener("click", async () => {
      const slug = button.dataset.copySlug;
      if (!slug) return;
      const url = `${window.location.origin}${toolkitSectionUrl(slug)}`;
      try {
        await navigator.clipboard.writeText(url);
        const label = button.querySelector(".toolkit-copy-link-label");
        if (label) {
          const prev = label.textContent;
          label.textContent = "Copied";
          window.setTimeout(() => {
            label.textContent = prev;
          }, 1600);
        }
      } catch {
        /* ignore */
      }
    });
  });
}

function setupFaqControls(): void {
  const expand = document.getElementById("toolkit-faq-expand-all");
  const collapse = document.getElementById("toolkit-faq-collapse-all");
  const items = [...document.querySelectorAll<HTMLDetailsElement>(".toolkit-faq-item")];
  expand?.addEventListener("click", () => items.forEach((d) => (d.open = true)));
  collapse?.addEventListener("click", () => items.forEach((d) => (d.open = false)));
}

function setupMobileNav(): void {
  const toggle = document.getElementById("toolkit-mobile-nav-toggle");
  const backdrop = document.getElementById("toolkit-sidebar-backdrop");
  toggle?.addEventListener("click", () => {
    if (document.getElementById("toolkit-sidebar")?.classList.contains("is-open")) {
      closeMobileNav();
    } else {
      openMobileNav();
    }
  });
  backdrop?.addEventListener("click", closeMobileNav);
}

let demoFormStartedAt = 0;

export function openDemoRequestModal(): void {
  const backdrop = document.getElementById("demo-request-modal");
  if (!backdrop) return;
  demoFormStartedAt = Date.now();
  const started = document.getElementById("demo-request-started-at") as HTMLInputElement | null;
  if (started) started.value = String(demoFormStartedAt);
  for (const id of ["demo-request-error", "demo-request-success"]) {
    const el = document.getElementById(id);
    if (el) {
      el.textContent = "";
      el.classList.add("hidden");
    }
  }
  backdrop.classList.remove("hidden");
  backdrop.setAttribute("aria-hidden", "false");
}

function closeDemoRequestModal(): void {
  const backdrop = document.getElementById("demo-request-modal");
  if (!backdrop) return;
  backdrop.classList.add("hidden");
  backdrop.setAttribute("aria-hidden", "true");
}

export function setupDemoRequestModal(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-open-demo-request]").forEach((btn) => {
    btn.addEventListener("click", openDemoRequestModal);
  });

  document.getElementById("demo-request-modal-close")?.addEventListener("click", closeDemoRequestModal);
  document.getElementById("demo-request-modal")?.addEventListener("click", (event) => {
    if (event.target === event.currentTarget) closeDemoRequestModal();
  });

  const form = document.getElementById("demo-request-form") as HTMLFormElement | null;
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const submit = document.getElementById("demo-request-submit") as HTMLButtonElement | null;
    const errEl = document.getElementById("demo-request-error");
    const okEl = document.getElementById("demo-request-success");
    if (errEl) {
      errEl.textContent = "";
      errEl.classList.add("hidden");
    }
    if (okEl) {
      okEl.textContent = "";
      okEl.classList.add("hidden");
    }
    if (submit) submit.disabled = true;

    const fd = new FormData(form);
    try {
      await submitDemoRequest({
        name: String(fd.get("name") ?? ""),
        email: String(fd.get("email") ?? ""),
        company: String(fd.get("company") ?? ""),
        message: String(fd.get("message") ?? ""),
        company_website: String(fd.get("company_website") ?? ""),
        form_started_at: Number(fd.get("form_started_at") ?? demoFormStartedAt),
        page_url: window.location.href,
      });
      if (okEl) {
        okEl.textContent = "Thanks—we'll be in touch shortly.";
        okEl.classList.remove("hidden");
      }
      form.reset();
    } catch (e) {
      if (errEl) {
        errEl.textContent = e instanceof Error ? e.message : "Unable to send request.";
        errEl.classList.remove("hidden");
      }
    } finally {
      if (submit) submit.disabled = false;
    }
  });
}

export function showToolkitPage(): void {
  const marketingPage = document.getElementById("static-marketing-page");
  const pricingPage = document.getElementById("static-pricing-page");
  const toolkitPage = document.getElementById("static-toolkit-page");
  const securityPage = document.getElementById("static-security-page");
  const changelogPage = document.getElementById("static-changelog-page");
  if (marketingPage) marketingPage.hidden = true;
  if (pricingPage) pricingPage.hidden = true;
  if (securityPage) securityPage.hidden = true;
  if (changelogPage) changelogPage.hidden = true;
  if (toolkitPage) toolkitPage.hidden = false;
}

export function setupToolkitPage(): void {
  const subtitle = document.getElementById("toolkit-page-subtitle");
  const meta = document.getElementById("toolkit-page-meta");
  const nav = document.getElementById("toolkit-sidebar-nav");
  const main = document.getElementById("toolkit-main");
  if (!subtitle || !meta || !nav || !main) return;

  subtitle.textContent = TOOLKIT_PAGE_SUBTITLE;
  meta.textContent = `${getToolkitFeatureCount()} features documented · ~${estimateToolkitReadingMinutes()} min read`;
  nav.innerHTML = renderSidebarNav();

  let featureIndex = -1;
  const sectionsHtml = toolkitSections
    .map((section) => {
      if (!section.isOverview) featureIndex += 1;
      return renderToolkitSection(section, featureIndex);
    })
    .join("");

  main.innerHTML = sectionsHtml + renderFaqSection();

  const slug = getToolkitSlug();
  applyToolkitGeoSeo(slug);
  setupToolkitNavigation();
  setupToolkitScrollSpy();
  setupToolkitSearch();
  setupCopyLinks();
  setupFaqControls();
  setupMobileNav();
  setupDemoRequestModal();

  requestAnimationFrame(() => {
    scrollToToolkitSlug(slug, false);
    if (slug !== "overview") {
      window.history.replaceState({ toolkitSlug: slug }, "", toolkitSectionUrl(slug));
    }
  });
}
